/**
 * A button that shows the user it is working.
 *
 * WHY THIS EXISTS
 * Reported against Vendor API integrations: clicking "Test" or "Sync now"
 * appeared to do nothing. The handlers were actually fine — they set a busy
 * flag and the buttons carried `disabled={busy}` — but nothing about the button
 * CHANGED. There was no `disabled:` styling, no spinner and no label change, so
 * a disabled button rendered identically to an active one. A UniFi probe can sit
 * on a 15-second connect timeout, so the user got fifteen silent seconds and
 * then a sudden alert. From the outside that is indistinguishable from a dead
 * button, and the natural response is to click it again.
 *
 * Two design decisions worth keeping:
 *
 * 1. PENDING STATE IS PER BUTTON, not per page. The old pattern used one page-
 *    wide `busy`, which disables every button on the screen when you click one —
 *    so even with styling you could not tell WHICH action was running. This
 *    component owns its own state, so the spinner appears on the thing you
 *    clicked.
 *
 * 2. IT SWALLOWS DOUBLE CLICKS. While an action is in flight, further clicks are
 *    ignored rather than queued. The reported symptom makes people click again,
 *    and "Sync now" twice is a duplicate job.
 *
 * The pending state always clears in a finally, so a thrown action leaves a
 * usable button rather than one stuck spinning forever.
 */
import { ReactNode, useCallback, useRef, useState } from "react";

/**
 * "raw" keeps the caller's own className and adds nothing but the behaviour —
 * spinner, disable, aria-busy. That matters for converting existing buttons in
 * bulk: the point of those edits is to add feedback, and silently restyling
 * dozens of buttons at the same time would make any visual regression
 * impossible to attribute.
 */
type Variant = "primary" | "secondary" | "danger" | "ghost" | "raw";
type Size = "xs" | "sm" | "md";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-blue-600 hover:bg-blue-700 text-white border border-transparent disabled:hover:bg-blue-600",
  secondary:
    "border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 disabled:hover:bg-transparent",
  danger:
    "border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:hover:bg-transparent",
  ghost:
    "text-blue-600 dark:text-blue-400 hover:underline disabled:hover:no-underline",
  // raw contributes nothing: the caller's className is the whole style.
  raw: "",
};

const SIZES: Record<Size, string> = {
  xs: "text-xs px-2 py-1 gap-1",
  sm: "text-sm px-3 py-1.5 gap-1.5",
  md: "text-sm px-4 py-2 gap-2",
};

function Spinner({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`animate-spin h-3 w-3 shrink-0 ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

export default function ActionButton({
  onAction,
  children,
  pendingLabel,
  variant = "secondary",
  size = "xs",
  disabled = false,
  title,
  className = "",
  type = "button",
}: {
  /** Async work to run. Pending state is derived from the returned promise. */
  onAction: () => unknown | Promise<unknown>;
  children: ReactNode;
  /** Label while running. Defaults to keeping the original label + a spinner. */
  pendingLabel?: string;
  variant?: Variant;
  size?: Size;
  disabled?: boolean;
  title?: string;
  className?: string;
  type?: "button" | "submit";
}) {
  const [pending, setPending] = useState(false);
  // A ref as well as state, because two clicks in the same tick both see the
  // stale `false` from state — the ref is updated synchronously.
  const inFlight = useRef(false);

  const run = useCallback(async () => {
    if (inFlight.current || disabled) return;
    inFlight.current = true;
    setPending(true);
    try {
      await onAction();
    } finally {
      // Always clears, so a thrown action can't leave a permanently spinning
      // button that the user has no way to recover from.
      inFlight.current = false;
      setPending(false);
    }
  }, [onAction, disabled]);

  return (
    <button
      type={type}
      onClick={run}
      disabled={disabled || pending}
      title={title}
      aria-busy={pending}
      className={
        variant === "raw"
          ? // Caller owns the styling. Only add flex when a spinner is actually
            // present, so a non-pending raw button renders byte-identically to
            // the plain <button> it replaced.
            `${pending ? "inline-flex items-center gap-1" : ""} ${className}`
          : `inline-flex items-center justify-center rounded transition-colors
             disabled:opacity-60 disabled:cursor-not-allowed
             ${pending ? "cursor-progress" : ""}
             ${SIZES[size]} ${VARIANTS[variant]} ${className}`
      }
    >
      {pending && <Spinner />}
      <span>{pending ? pendingLabel || children : children}</span>
    </button>
  );
}

/**
 * Same behaviour for callers that need to keep their own markup — a hook that
 * hands back a pending flag and a wrapped runner.
 *
 * Useful where the control is not a plain button (a row action rendered as a
 * link, a menu item) and swapping the element would change the layout.
 */
export function useAction() {
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);

  const run = useCallback(async (fn: () => unknown | Promise<unknown>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    try {
      await fn();
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, []);

  return { pending, run };
}
